import React, { forwardRef, useCallback, useId, useRef } from "react";
import { FieldProps } from "../FieldBase/FieldProps";
import { FormControl } from "../FormControl";
import { FormHelperText } from "../FieldBase/FormHelperText";
import { InputBase, InputBaseProps } from "../InputBase";
import { InputLabel } from "../InputLabel";
import { useDitectInputTouched } from "../FieldBase/useDitectInputTouched";
import { useFieldGroup } from "../FieldGroup";
import { useInputValue } from "../hooks/useInputValue";
import { useMergeRefs } from "../hooks/useMergeRefs";
import { useValidation } from "../FieldBase/useValidation";

export interface TextFieldProps
  extends
    FieldProps<string>,
    Omit<InputBaseProps, "value" | "onChange" | "defaultValue"> {}

export const TextField = forwardRef<HTMLDivElement, TextFieldProps>(
  (props, rootRef) => {
    const {
      label,
      style,
      className,
      onChange,
      onFocus,
      defaultValue,
      value: _propsValue,
      disabled,
      validationMessages,
      customValidations,
      type = "text",
      helperText,
      invalid,
      loading: propsLoading,
      readOnly: propsReadonly,
      id,
      inputRef,
      ...restProps
    } = props;

    const ref = useRef<HTMLInputElement>(null);
    const refs = useMergeRefs(inputRef, ref);
    const [value, setValue, isControlled] = useInputValue(props);
    const { loading, readOnly } = useFieldGroup({
      loading: propsLoading,
      readOnly: propsReadonly,
      onLoaded: () => {
        if (isControlled === false) {
          setValue(defaultValue);
        }
      },
    });
    const validation = useValidation({
      ref,
      validationMessages,
      customValidations,
      value,
    });
    const handleChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.currentTarget;
        setValue(value);
        if (onChange) {
          onChange(value, event);
        }
      },
      [onChange, setValue],
    );
    const [touched, inputProps] = useDitectInputTouched(onFocus);

    const internalInputId = useId();
    const inputId = id ?? internalInputId;

    return (
      <FormControl style={style} className={className} ref={rootRef}>
        <InputLabel
          htmlFor={inputId}
          aria-disabled={disabled}
          required={props.required}
          loading={loading}
        >
          {label}
        </InputLabel>
        <InputBase
          id={inputId}
          disabled={disabled}
          loading={loading}
          readOnly={readOnly}
          type={type}
          value={value ?? ""}
          onChange={handleChange}
          data-invalid={!validation.valid}
          ref={refs}
          {...restProps}
          {...inputProps}
        />
        <FormHelperText
          invalid={!validation.valid || invalid}
          loading={loading}
          touched={touched}
          value={value}
        >
          {
            // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
            validation.message || helperText
          }
        </FormHelperText>
      </FormControl>
    );
  },
);
